/* eslint-disable react-hooks/exhaustive-deps */
// @vendors
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// @store
import useOnboardingStore from "../../store/useOnboardingStore"; // Importa tu store
import { NextStepGuardRouteString } from "../../guards/NextStepGuard";
import { useSpinnerStore } from "../../store/useSpinnerStore";

// @config
import routesConfig from "../../config/routes.config"; // Asegúrate de que esté importado correctamente

// @components
import locationServices from "../../services/location.service"
import { ICities, IDepartments } from "../../types/locations.types";

const StyledInput: React.FC<{
  label: string;
  value: string;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  placeholder: string;
  disabled?: boolean;
}> = ({ label, value, onChange, placeholder, disabled }) => (
  <div className="flex flex-col gap-2">
    <label className="text-cTextSecondary dark:text-dark-cTextSecondary">
      {label}
    </label>
    <input
      type="text"
      value={value}
      onChange={onChange}
      className="w-full py-2 border-b text-cTextSecondary/50 dark:text-dark-cTextSecondary/50 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0 disabled:bg-white"
      placeholder={placeholder}
      disabled={disabled}
    />
  </div>
);

const LocalizationOnboardingPage: React.FC<{ onNext: () => void }> = ({ onNext }) => {
  const { t } = useTranslation();
  const { updateLocation, isLoading } = useOnboardingStore();
  const [country, setCountry] = useState("Colombia"); // Precargamos "Colombia"
  const [state, setState] = useState("");
  const [city, setCity] = useState("");
  const [address, setAddress] = useState("");
  const [locationError, setLocationError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { openSpinner } = useSpinnerStore();
  const [listCities, setListCities] = useState<ICities[]>([]);
  const isFormEmpty = !country || !state || !city || !address;

  const handleCancel = () => {
    localStorage.clear();
    window.location.href = routesConfig.home;
  };

  const getDepartments = async () => {
    openSpinner(true);
    try {
      const response = await locationServices.getAllDepartments();
      const data = response.data;
      const defaultDepartment = data?.find((item: IDepartments) => item?.name === "Antioquia");
      setState(defaultDepartment?.name || "");
      getCities(defaultDepartment?.id ?? 2);
    } catch (error) {
      openSpinner(false);
      toast.error(t("error.callList"));
    }
  }

  const getCities = async (departmentId: number) => {
    openSpinner(true);
    try {
      const response = await locationServices.getCityByDepartment(departmentId);
      setListCities(response.data);
      openSpinner(false);
    } catch (error) {
      openSpinner(false);
      toast.error(t("error.callList"));
    }
  }

  useEffect(() => {
    getDepartments();
  }, [])

  /* const isValidLocation = (country: string, state: string, city: string) => {
    return (
      country.toLowerCase() === "colombia" &&
      state.toLowerCase() === "antioquia" &&
      ALLOWED_CITIES.some(
        (allowedCity) => city.toLowerCase() === allowedCity.toLowerCase()
      )
    );
  }; */

  useEffect(() => {
    // Verificamos si los permisos de ubicación están habilitados
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          getLocationData(latitude, longitude);
        },
        (error) => {
          setLocationError(t("onboarding.localization.locationError"));
        }
      );
    } else {
      setLocationError(t("onboarding.localization.geoNotSupported"));
    }
  }, [t]);

  const getLocationData = async (lat: number, lng: number) => {
    const URL = `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyChrd6n58jSLu3G7s1EbQQxiZqmDTl5aDg`;
    try {
      const response = await axios.get(URL);
      if (response?.data?.results.length > 0) {
        const addressComponents = response.data.results[0].address_components;
        const formattedAddress = `${addressComponents[1].short_name} # ${addressComponents[0].short_name}`;
        /* const extractedState = addressComponents.find((component: any) =>
          component.types.includes("administrative_area_level_1")
        )?.long_name; */
        const extractedCity = addressComponents.find((component: any) =>
          component.types.includes("administrative_area_level_2")
        )?.long_name;
        //setState(extractedState || "");
        setCity(extractedCity || "");
        setAddress(formattedAddress);
      }
    } catch (error) {
      toast.error('Error al obtener los datos de Google Maps');
      setLocationError(t("onboarding.localization.fetchLocationError"));
    }
  };

  const handleCountryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedCountry = e.target.value;
    setCountry(selectedCountry);
    //setCountryCode(countryCodes[selectedCountry] || "");
  };

  const handleContinue = async () => {
    openSpinner(true);
    if (!isFormEmpty) {
      try {
        await updateLocation({
          departmentNameFromGM: state,
          countryNameFromGM: country,
          cityNameFromGM: city,
          address: address,
        });
        const redirectTo = NextStepGuardRouteString();
        navigate(redirectTo);
        onNext(); // Call onNext after successful submission
        openSpinner(false);
      } catch (error) {
        openSpinner(false);
        toast.error(t("error.postData"));
      }
    } else {
      openSpinner(false);
      setLocationError(t("onboarding.localization.fillAllFields"));
    }
  };

  const normalizeText = (text: string) => {
    return text.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  }

  return (
    <>
      <div className="w-full px-4 lg:max-w-[80%] max-w-[100%] mx-auto lg:mt-36 mt-20 bg-cBackground dark:bg-dark-cBackground">
        <div className="grid grid-cols-1 md:grid-cols-2 items-center justify-center ">
          <div className="flex flex-col items-center justify-center gap-8">
            {/* <h1 className="text-3xl font-bold mb-4">{t('onboarding.localization.welcomeTitle')}</h1> */}
            <img
              src="/assets/img/location.png"
              alt="Location"
              className="w-auto lg:h-2/3 mx-auto h-[100px]"
            />
            <h1 className="text-[18px] font-medium text-cText dark:text-dark-cText">
              {t("onboarding.localization.title")}
            </h1>
            <p className="w-full lg:px-8 px-0 line-clamp-3 text-pretty text-justify text-cTextSecondary dark:text-dark-cTextSecondary text-sm">
              {t("onboarding.localization.description")}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 text-[14px]">
            <StyledInput
              label={t("onboarding.localization.country")}
              value={country}
              onChange={handleCountryChange}
              placeholder={t("onboarding.localization.enterCountry")}
              disabled={true}
            />
            <StyledInput
              label={t("onboarding.localization.department")}
              value={state}
              onChange={(e) => setState(e.target.value)}
              placeholder={t("onboarding.localization.enterDepartment")}
              disabled={true}
            />
            <div className="flex flex-col gap-2">
              <label className="text-cTextSecondary dark:text-dark-cTextSecondary text-[14px]">
                {t("onboarding.localization.city")}
              </label>
              <select
                value={normalizeText(city) || ''}
                onChange={(e) => setCity(e.target.value)}
                className="w-full py-2 border-b text-cTextSecondary/50 dark:text-dark-cTextSecondary/50 placeholder:text-cTextSecondary/50 dark:placeholder-dark-cTextSecondary/50 focus:outline-none focus:ring-0"
              >
                <option className="text-[14px]" value="">{t("onboarding.localization.enterCity")}</option>
                {listCities.map((city) => (
                  <option
                    className=" w-full truncate text-[14px] text-textOnWarning"
                    key={city?.name}
                    value={normalizeText(city?.name)}
                  >
                    {city?.name}
                  </option>
                ))}
              </select>
            </div>
            <StyledInput
              label={t("onboarding.localization.address")}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder={t("onboarding.localization.enterAddress")}
            />

            {locationError && <p className="text-red-500">{locationError}</p>}

            <div className="flex justify-center gap-4 mt-8">
              <button
                onClick={handleContinue}
                disabled={isFormEmpty || isLoading}
                className={`px-8 py-3 rounded-lg w-full cursor-pointer ${isFormEmpty
                  ? "bg-gray-300 cursor-not-allowed"
                  : "bg-bgBrand hover:bg-bgBrandHover text-white"
                  }`}
              >
                {t("onboarding.localization.continueButton")}
              </button>
              <button
                onClick={handleCancel}
                className="px-8 py-3 rounded-lg w-full border border-bgBrand text-bgBrand hover:bg-gray-50"
              >
                {t("onboarding.localization.cancelButton")}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LocalizationOnboardingPage;
